/**
 * B2 (akış araştırması §13): "Partner arıyorum" hızlı gönderisi. Kullanıcı gün ve saat seçer; sunucu bunu
 * İstanbul takvimiyle kesin tarihe çevirir (`tarih` başlangıç, `bitis` son gün). Kart, `bitis` geçince
 * "süresi geçti" olur ve teklif düğmesi kalkar. Gün sözcükleri yalnız seçimde; kartta tarih gösterilir.
 */
export const PARTNER_DAYS = ['bugun', 'yarin', 'hafta_sonu'] as const;
export const PARTNER_TIMES = ['sabah', 'ogle', 'aksam', 'esnek'] as const;
export const PARTNER_NOTE_MAX = 140;
export type PartnerDay = (typeof PARTNER_DAYS)[number];
export type PartnerTime = (typeof PARTNER_TIMES)[number];
export type FeedPartner = { gun: PartnerDay; zaman: PartnerTime; tarih: string; bitis: string };

const DAY = 24 * 60 * 60 * 1000;
const ISTANBUL_OFFSET = 3 * 60 * 60 * 1000;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** İstanbul günü `YYYY-MM-DD`. */
export const istanbulDate = (ms: number) =>
  new Date(ms + ISTANBUL_OFFSET).toISOString().slice(0, 10);

/** Seçimi kesin tarih aralığına çevirir; hafta sonu: bu cumartesi–pazar (pazar günü yalnız bugün). */
export function partnerRange(gun: PartnerDay, now: number): { tarih: string; bitis: string } {
  if (gun === 'bugun') return { tarih: istanbulDate(now), bitis: istanbulDate(now) };
  if (gun === 'yarin') return { tarih: istanbulDate(now + DAY), bitis: istanbulDate(now + DAY) };
  const weekday = new Date(now + ISTANBUL_OFFSET).getUTCDay(); // 0 pazar, 6 cumartesi
  if (weekday === 0) return { tarih: istanbulDate(now), bitis: istanbulDate(now) };
  return {
    tarih: istanbulDate(now + (6 - weekday) * DAY),
    bitis: istanbulDate(now + (7 - weekday) * DAY),
  };
}

/** İstekten gelen seçimi doğrular ve tarih ekler; geçersizse null. */
export function buildPartner(value: unknown, now: number): FeedPartner | null {
  const input = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
  if (
    !PARTNER_DAYS.includes(input.gun as PartnerDay) ||
    !PARTNER_TIMES.includes(input.zaman as PartnerTime)
  )
    return null;
  const gun = input.gun as PartnerDay;
  return { gun, zaman: input.zaman as PartnerTime, ...partnerRange(gun, now) };
}

/** Kayıttan okunan partner alanı (bozuk kayıt gösterilmez). */
export function toPartner(value: unknown): FeedPartner | undefined {
  const input = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
  if (
    !PARTNER_DAYS.includes(input.gun as PartnerDay) ||
    !PARTNER_TIMES.includes(input.zaman as PartnerTime)
  )
    return undefined;
  if (
    typeof input.tarih !== 'string' ||
    !DATE.test(input.tarih) ||
    typeof input.bitis !== 'string' ||
    !DATE.test(input.bitis)
  ) {
    return undefined;
  }
  return {
    gun: input.gun as PartnerDay,
    zaman: input.zaman as PartnerTime,
    tarih: input.tarih,
    bitis: input.bitis,
  };
}

export const partnerExpired = (partner: Pick<FeedPartner, 'bitis'>, now: number) =>
  istanbulDate(now) > partner.bitis;
